import {
    useMemo,
    forwardRef,
    type ForwardRefRenderFunction,
    type CSSProperties,
    type ReactNode,
} from 'react';
import classnames from 'classnames';
import { colors, type SassyColorNames } from 'dibs-sassy/exports/colors';

import styles from './main.scss';

type Props = {
    children: ReactNode;
    /** Controls list marker background property */
    markerBg?: SassyColorNames;
    /** Controls list marker color property */
    markerFg?: SassyColorNames;
    /**
     * Border shorthand CSS property
     * @see https://developer.mozilla.org/docs/Web/CSS/border
     */
    markerBorder?: CSSProperties['border'];
    /** Applied to the OrderedList's OL element */
    className?: string;
    /** Applied to the OrderedList's OL element */
    style?: CSSProperties;
    /** Label for automated testing */
    dataTn?: string;
    /** Controls list marker background property */
};

/**
 * features:
 *   List marker design is pure CSS, which reduces logic, markup, accessibility, and layout considerations
 *   Marker is rendered in an LI's ::before pseudo-element
 */
export const OrderedListComponent: ForwardRefRenderFunction<HTMLOListElement, Props> = (
    { children, markerBg, markerFg, markerBorder, dataTn, className, style: styleProp },
    ref
) => {
    const style = useMemo(
        () => ({
            ['--dibs-ordered-list-marker-border']: markerBorder,
            ['--dibs-ordered-list-marker-bg']:
                markerBg && markerBg in colors ? colors[markerBg] : undefined,
            ['--dibs-ordered-list-marker-fg']: markerFg && colors[markerFg],
            ...styleProp,
        }),
        [markerBg, markerBorder, markerFg, styleProp]
    );
    return (
        <ol ref={ref} className={classnames(styles.list, className)} data-tn={dataTn} style={style}>
            {children}
        </ol>
    );
};

export const OrderedList = forwardRef(OrderedListComponent);
